import { useTranslation } from 'react-i18next';
import { formatDate } from '../../core/helpers';
import { normalizeDateFE } from '../../core/normalization';
import { SellerFilingReportToConfirm } from '../../core/types';
import { ReactComponent as Agenda } from '../../icons/agenda.svg';
import Button from '../Button';
import Modal from '../Modal/Modal';
import { ReactComponent as TipIcon } from '../../icons/tip.svg';
import './AgendaModal.scss';
import { useEffect, useState } from 'react';

interface Props {
  report: SellerFilingReportToConfirm | undefined;
  isModalOpened: boolean;
  closeModal: () => void;
  onClickConfirm: () => void;
  isConfirming: boolean;
}

export default function AgendaModal({
  report,
  isModalOpened,
  closeModal,
  onClickConfirm,
  isConfirming,
}: Props) {
  const { t } = useTranslation();
  const [confirmation, setConfirmation] = useState(false);

  useEffect(() => {
    if (isModalOpened) {
      setConfirmation(false);
    }
  }, [isModalOpened]);

  if (!report) return null;

  const renderPeriod = () => {
    let formatStr = 'MMMM yyyy';

    if (report.period.type === 'year') {
      formatStr = 'yyyy';
    }

    if (report.period.type === 'quarter') {
      formatStr = 'QQQ yyyy';
    }

    return formatDate(normalizeDateFE(report.period.from_date), formatStr);
  };

  return (
    <Modal
      isOpened={isModalOpened}
      close={closeModal}
      title='Agenda'
      isLoading={isConfirming}
      name='Agenda'
    >
      {confirmation ? (
        <div
          className='d-flex align-items-center justify-content-center flex-column mb-3'
          style={{
            minHeight: 450,
          }}
        >
          <div className='Text--20 fw-800 multiline mb-3 text-center'>
            {t('Confirm submission of German VAT report on Agenda portal')}
          </div>
          <Button
            data-testid='agenda-modal-confirm-btn'
            onClick={onClickConfirm}
            disabled={isConfirming}
          >
            {t('Confirm')}
          </Button>
        </div>
      ) : (
        <div className='mx-3 pt-3 pb-4 px-3'>
          <div className='d-flex align-items-center mb-2'>
            <div className='Text--20 fw-800'>
              {formatDate(normalizeDateFE(report.deadline_date), 'MMMM yyyy')}
            </div>
            {report.agenda_confirmed && (
              <div className='ms-auto Text--green fw-600 d-flex align-items-center'>
                <TipIcon className='AgendaModal__icon' />
                &nbsp;
                {t('Submitted')}
              </div>
            )}
          </div>
          <div className='d-flex align-items-center'>
            <div className='fw-800 me-2'>{t('Submission with')}</div>
            <Agenda />
          </div>
          <div className='mb-3'>
            {t(
              'Please prepare and submit the advance return for VAT to the tax authorities by releasing it in the Agenda Business Portal.'
            )}
          </div>
          <div className='mb-4'>
            <ol className='AgendaModal__list'>
              <li>
                {t('Click on')}{' '}
                <a
                  href='https://agenda-unternehmens-portal.de'
                  rel='noreferrer'
                  target='_blank'
                  className='Link--blue'
                >
                  agenda-unternehmens-portal.de
                </a>
              </li>
              <li>
                {t(
                  'Enter your access data for the Agenda Unternehmens-Portal.'
                )}
              </li>
              <li>
                {t('Select "Accounting" and the period "*period*".').replace(
                  '*period*',
                  renderPeriod()
                )}
              </li>
              <li>
                {t(
                  'Check the correctness and completeness of the accounting using the Primanota.'
                )}
              </li>
              <li>
                {t(
                  'Create and submit the advance VAT return to the tax authorities.'
                )}
              </li>
            </ol>
          </div>
          <div className='row row-cols-auto gx-2'>
            {!report.agenda_confirmed && (
              <div className='col'>
                <Button
                  onClick={() => {
                    setConfirmation(true);
                  }}
                  data-testid='agenda-modal-submit-btn'
                >
                  {t('Mark as submitted')}
                </Button>
              </div>
            )}
            <div className='col'>
              <Button styling='outline' onClick={closeModal}>
                {t('Close')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Modal>
  );
}
